import React, { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, X, BookOpen, MessageCircle, FileText, UserPlus, ClipboardList, CalendarPlus } from 'lucide-react';
import useAuthStore from '../store/authStore';
import { useViewport } from '../hooks/useViewport';
import { trackEvent } from '../lib/telemetry';
import api from '../services/api';

// App.jsx imports this component statically, so anything it pulls in lands in
// the main bundle — and LessonModal alone is a thousand lines. The modals are
// only needed once a quick action is tapped, so they are split out and mounted
// on demand; nothing is fetched for a user who never opens the FAB.
const QuickAddStudentModal = lazy(() => import('./Dashboard/QuickAddStudentModal'));
const ClientQuickAddModal = lazy(() => import('./Dashboard/ClientQuickAddModal'));
const LessonModal = lazy(() => import('./Dashboard/LessonModal'));
const ClientIntakeModal = lazy(() => import('./Sport/ClientIntakeModal'));
const SportScheduleModal = lazy(() => import('./Sport/SportScheduleModal'));

/**
 * FabRadial — Phase 2.5
 *
 * Floating action button anchored to the bottom-end of the viewport (above
 * the bottom nav). Tapping it fans out 3-4 quick actions tailored to the
 * current user's role and center type.
 *
 * An action either opens a modal right here (`modal`) or navigates (`to`).
 * Prefer the modal: these used to navigate to `/students?new=1` and
 * `/lessons?new=1`, but nothing ever read those query parameters, so the tap
 * landed you on a plain list with nothing open — the action silently did
 * nothing. A quick action should complete where you already are.
 *
 * The two that still navigate are the ones whose whole screen IS the workflow:
 * bulk WhatsApp (recipient picker, template, variables) and the survey builder.
 *
 * Why a separate component from `EntityDetail/Fab.jsx`:
 *   - Entity-detail FAB is `position: sticky` inside a scroll area
 *     (per-page primary action — e.g. "Add note to this student").
 *   - This one is `position: fixed`, app-global, and represents the most
 *     common actions a user wants regardless of which page they're on.
 *
 * Auto-hides on routes that have a richer in-page primary action:
 *   - /students/:id, /clients/:id, /groups/:id  (entity detail)
 *   - /attendance/:lessonId  (sticky submit toolbar)
 *   - /login, /help, /public/*  (no auth context anyway)
 *
 * Only renders on phones + tablets — desktop uses the sidebar + Cmd+K.
 *
 * Sits just above the bottom nav (z 1000) and below every overlay — the side
 * drawer, modals, the group picker and the command palette all layer on top,
 * so the FAB never floats over a sheet the user has open.
 */

const HIDE_PATTERNS = [
  /^\/students\/[^/]+/,
  /^\/clients\/[^/]+/,
  /^\/groups\/[^/]+/,
  /^\/attendance\/[^/]+/,
  /^\/login/,
  /^\/help/,
  /^\/public\//,
  /^\/intake\//,
  /^\/sign-contract\//,
];

function getActions({ user, centerType, learningSubType, t }) {
  const isTeacher = user?.role === 'teacher';
  const isLearning = centerType === 'learning';
  const isSport = isLearning && learningSubType === 'sport';
  const isHair = centerType === 'hair_transplant';

  if (isTeacher && !isHair) {
    return [
      { id: 'attendance', label: t('fab.markAttendance', 'Mark attendance'), icon: BookOpen, to: '/teacher/attendance' },
      { id: 'whatsapp',   label: t('fab.sendWhatsapp',   'Send WhatsApp'),    icon: MessageCircle, to: '/whatsapp/bulk-send' },
      { id: 'checklist',  label: t('fab.openChecklists', 'Open checklists'),  icon: ClipboardList, to: '/teacher/checklists' },
    ];
  }

  // Sport managers: the dashboard's two primary actions come first.
  if (isSport) {
    return [
      { id: 'client-intake',  label: t('sport.dashboard.intake', 'Client intake'), icon: UserPlus,     modal: 'intake' },
      { id: 'sport-schedule', label: t('sport.dashboard.schedule', 'Schedule sessions'), icon: CalendarPlus, modal: 'sportSchedule' },
      { id: 'whatsapp',       label: t('fab.sendWhatsapp', 'Send WhatsApp'), icon: MessageCircle, to: '/whatsapp/bulk-send' },
    ];
  }

  if (isLearning) {
    return [
      { id: 'add-student', label: t('fab.addStudent', 'Add student'), icon: UserPlus,     modal: 'student' },
      { id: 'add-lesson',  label: t('fab.newLesson',  'New lesson'),  icon: CalendarPlus, modal: 'lesson' },
      { id: 'whatsapp',    label: t('fab.sendWhatsapp', 'Send WhatsApp'), icon: MessageCircle, to: '/whatsapp/bulk-send' },
      { id: 'new-survey',  label: t('fab.newSurvey',  'New survey'),  icon: FileText,     to: '/surveys?new=1' },
    ];
  }

  // therapy / hair / fallback — show generic admin actions
  return [
    { id: 'add-client', label: t('fab.addClient',  'Add client'),    icon: UserPlus,     modal: 'client' },
    { id: 'whatsapp',   label: t('fab.sendWhatsapp', 'Send WhatsApp'), icon: MessageCircle, to: '/whatsapp/bulk-send' },
    { id: 'new-survey', label: t('fab.newSurvey',  'New survey'),    icon: FileText,     to: '/surveys?new=1' },
  ];
}

export default function FabRadial() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, centerType, learningSubType } = useAuthStore();
  const { isMobile: isPhone, isTablet } = useViewport();
  const [open, setOpen] = useState(false);
  const [activeModal, setActiveModal] = useState(null);
  // LessonModal needs the centre's groups/teachers/students to populate its
  // selects. They are fetched the first time the lesson modal is opened rather
  // than on mount, so the FAB costs nothing on every other screen.
  const [lessonData, setLessonData] = useState({ groups: [], teachers: [], students: [], lessons: [] });
  const isRTL = i18n.language === 'ar' || i18n.language === 'he';

  // Calendar renders its own "create event" FAB in the same corner, so on a
  // phone the two would sit there together — one mirrored to each bottom
  // corner, since they pin to opposite inline edges. That page-level FAB is
  // itself phone-only (it renders under 768px), so on a tablet this one has to
  // stay or the calendar ends up with no FAB at all.
  const calendarOwnsTheFab = isPhone && /^\/calendar/.test(location.pathname);

  const hidden =
    !token ||
    (!isPhone && !isTablet) ||
    calendarOwnsTheFab ||
    HIDE_PATTERNS.some((re) => re.test(location.pathname));

  // Close on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Esc closes
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  useEffect(() => {
    if (activeModal !== 'lesson' && activeModal !== 'sportSchedule') return undefined;
    let cancelled = false;
    (async () => {
      const unwrap = (r) => (Array.isArray(r) ? r : r?.data || []);
      const [groups, teachers, students, lessons] = await Promise.allSettled([
        api.get('/groups'),
        api.get('/teachers'),
        api.get('/students?limit=500&enrich=0'),
        api.get('/lessons?limit=200'),
      ]);
      if (cancelled) return;
      setLessonData({
        groups: groups.status === 'fulfilled' ? unwrap(groups.value) : [],
        teachers: teachers.status === 'fulfilled' ? unwrap(teachers.value) : [],
        students: students.status === 'fulfilled' ? unwrap(students.value) : [],
        lessons: lessons.status === 'fulfilled' ? unwrap(lessons.value) : [],
      });
    })();
    return () => { cancelled = true; };
  }, [activeModal]);

  const onAction = useCallback((action) => {
    trackEvent('fab.select', { id: action.id, to: action.to, modal: action.modal });
    setOpen(false);
    if (action.modal) setActiveModal(action.modal);
    else navigate(action.to);
  }, [navigate]);

  if (hidden) return null;

  const actions = getActions({ user, centerType, learningSubType, t });
  if (actions.length === 0) return null;

  return (
    <>
      {/* Backdrop — captures outside-tap to close */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          aria-hidden="true"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.35)',
            backdropFilter: 'blur(2px)',
            WebkitBackdropFilter: 'blur(2px)',
            zIndex: 1005,
            animation: 'fabBackdropIn 180ms var(--ease-decelerate, cubic-bezier(0,0,0.2,1))',
          }}
        />
      )}

      {/* Stack: actions appear above the trigger, vertically */}
      <div
        style={{
          position: 'fixed',
          bottom: 'calc(80px + env(safe-area-inset-bottom, 0px))',
          insetInlineEnd: 'var(--space-4)',
          zIndex: 1010,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 12,
          pointerEvents: 'none',
        }}
      >
        {open && actions.map((a, i) => {
          const Icon = a.icon;
          // Stagger appearance for a subtle fan-in feel
          const delay = `${i * 35}ms`;
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => onAction(a)}
              aria-label={a.label}
              style={{
                pointerEvents: 'auto',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 16px 10px 14px',
                background: 'var(--surface-raised)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-light)',
                borderRadius: 'var(--radius-full)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                cursor: 'pointer',
                boxShadow: 'var(--shadow-lg)',
                minHeight: 44,
                animation: `fabActionIn 220ms var(--ease-emphasized, cubic-bezier(0.34,1.56,0.64,1)) both`,
                animationDelay: delay,
                flexDirection: isRTL ? 'row-reverse' : 'row',
              }}
            >
              <span style={{
                width: 32, height: 32, borderRadius: '50%',
                background: 'var(--primary-50)',
                color: 'var(--primary-dark)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Icon size={16} aria-hidden="true" />
              </span>
              <span>{a.label}</span>
            </button>
          );
        })}

        {/* Trigger */}
        <button
          type="button"
          onClick={() => setOpen((o) => {
            if (!o) trackEvent('fab.open', {});
            return !o;
          })}
          aria-label={open ? t('fab.close', 'Close quick actions') : t('fab.open', 'Quick actions')}
          aria-expanded={open}
          style={{
            pointerEvents: 'auto',
            width: 56,
            height: 56,
            borderRadius: 28,
            border: 'none',
            background: open ? 'var(--neutral-700)' : 'var(--primary-dark)',
            color: 'var(--text-inverse)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 8px 24px rgba(17, 24, 39, 0.25), 0 2px 4px rgba(17, 24, 39, 0.1)',
            transition: 'background-color var(--motion-quick), transform var(--motion-quick)',
            transform: open ? 'rotate(45deg)' : 'rotate(0deg)',
          }}
        >
          {open ? <X size={22} aria-hidden="true" /> : <Plus size={22} aria-hidden="true" />}
        </button>
      </div>

      {/* Mounted only while open, so each chunk is fetched on first use. */}
      <Suspense fallback={null}>
        {activeModal === 'student' && (
          <QuickAddStudentModal isOpen onClose={() => setActiveModal(null)} />
        )}

        {activeModal === 'client' && (
          <ClientQuickAddModal isOpen onClose={() => setActiveModal(null)} endpoint="/students" />
        )}

        {activeModal === 'intake' && (
          <ClientIntakeModal isOpen onClose={() => setActiveModal(null)} />
        )}

        {activeModal === 'sportSchedule' && (
          <SportScheduleModal
            isOpen
            onClose={() => setActiveModal(null)}
            groups={lessonData.groups}
            teachers={lessonData.teachers}
            students={lessonData.students}
          />
        )}

        {activeModal === 'lesson' && (
          <LessonModal
            isOpen
            onClose={() => setActiveModal(null)}
            user={user}
            groups={lessonData.groups}
            teachers={lessonData.teachers}
            students={lessonData.students}
            lessons={lessonData.lessons}
            onSave={async (payload) => {
              if (payload.groupId && payload.groupId !== 'individual') {
                await api.post(`/lessons?groupId=${payload.groupId}`, payload);
              } else {
                await api.post('/lessons', payload);
              }
            }}
          />
        )}
      </Suspense>

      <style>{`
        @keyframes fabBackdropIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes fabActionIn {
          from { opacity: 0; transform: translateY(8px) scale(0.92); }
          to   { opacity: 1; transform: translateY(0)    scale(1); }
        }
      `}</style>
    </>
  );
}
